import { Component, OnInit, NgZone, Inject, ChangeDetectorRef, PLATFORM_ID, Optional, REQUEST } from "@angular/core";
import { MainService } from "./z-services/main.service";
import { GoogleAnalyticsService } from "./z-services/google-analytics.service";
import { SeoService } from "./z-services/seo.service";
import { buildLocalizedPath, getApiHostByHostName, getRequestUrl, normalizeLanguageCode, parseRouteState } from "./z-services/site-config";
import { Router, NavigationEnd } from "@angular/router";
import { filter, catchError } from "rxjs/operators";
import { EMPTY } from "rxjs";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { dlgAlert } from "./z-dlg-alert/z-dlg-alert.component";

/* ------------------------------------------------------------------ */
/*  User model                                                        */
/* ------------------------------------------------------------------ */
export class User {
  public name: string;
  public email: string;
  public password: string;
  public hobbies: string;
}

@Component({
  standalone: false,
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
})
export class AppComponent implements OnInit {
  /* ----------------------- state ------------------------------------ */
  curPage: string;
  allDataLoadedVars: string[] = [];
  allDataLoaded = false;
  allProcessesDoneVars: string[] = [];
  allProcessesDone = false;

  selLang = "";
  defLang = "en";
  browserLang: string;
  mainName = "GLT Design";

  tempLang: any;
  langData: any;
  langDataClear: any;
  langsAvailable: Array<{ code: string; lang_lang: string }> = [];

  sApiServer: string;
  sApiHost: string;
  sHostName = "";

  model = new User();

  constructor(
    public mainService: MainService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private seoService: SeoService,
    private router: Router,
    private domSanitizer: DomSanitizer,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null,
    public matDialog: MatDialog,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    const requestUrl = getRequestUrl(this.request);
    const initialPathname = isPlatformBrowser(this.platformId)
      ? window.location.pathname
      : (null !== requestUrl ? requestUrl.pathname : "/");
    const initialRouteState = parseRouteState(initialPathname, this.defLang);

    /* boot */
    if (isPlatformBrowser(this.platformId)) {
      this.selLang = this.getCookie("selLang");
    }
    if (isPlatformBrowser(this.platformId)) {
      this.sHostName = window.location.hostname;
    } else if (null !== requestUrl) {
      this.sHostName = requestUrl.hostname;
    }
    this.setEnvParams();
    this.curPage = initialRouteState.pageKey;
    this.browserLang = initialRouteState.hasLanguagePrefix ? initialRouteState.languageCode : "";
    if (initialRouteState.hasLanguagePrefix) {
      this.selLang = initialRouteState.languageCode;
    }
    if ("" === this.selLang) {
      this.selLang = isPlatformBrowser(this.platformId)
        ? navigator.language.substring(0, 2).toLowerCase()
        : initialRouteState.languageCode;
    }
    this.selLang = normalizeLanguageCode(this.selLang, initialRouteState.languageCode);
    this.langRequest();

    /* show startup spinner only once */
    this.spinnerLoader(false, "window");
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener("load", () => this.spinnerLoader(true, "window"));
    } else {
      this.spinnerLoader(true, "window");
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Base-64 helpers                                                   */
  /* ------------------------------------------------------------------ */
  b64EncodeUnicode(str: any) {
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_m, p1) =>
        String.fromCharCode(("0x" + p1) as any)
      )
    );
  }
  b64DecodeUnicode(str: string) {
    return decodeURIComponent(
      atob(str)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  }
  bypassHtmlSanitizing(html: string): SafeHtml {
    return this.domSanitizer.bypassSecurityTrustHtml(html);
  }

  /* ------------------------------------------------------------------ */
  /*  Cookie helpers (unchanged)                                        */
  /* ------------------------------------------------------------------ */
  clearCookie(cname = ""): boolean {
    if (!isPlatformBrowser(this.platformId)) { return false; }
    let ret = false;
    this.document.cookie.split(";").forEach((raw) => {
      const key = raw.replace(/^\s+/, "").split("=")[0];
      if (cname === "" || key === cname) {
        this.setCookie(key, "", 0);
        ret = true;
      }
    });
    return ret;
  }
  getCookie(cname = ""): string {
    if (!isPlatformBrowser(this.platformId)) { return ""; }
    const name = cname + "=";
    for (let c of this.document.cookie.split(";")) {
      while (c.charAt(0) === " ") c = c.substring(1);
      if (c.indexOf(name) === 0) return c.substring(name.length);
    }
    return "";
  }
  getCookieInt(cname = ""): number | null {
    const v = this.getCookie(cname);
    return v ? parseInt(v, 10) : null;
  }

  /* ------------------------------------------------------------------ */
  /*  Language data & first spinner                                     */
  /* ------------------------------------------------------------------ */
  langRequest() {
    this.spinnerLoader(false, "lang");
    const url = this.sApiServer + "lang.php";

    this.mainService
      .getJSON_GET(url + "?act=getAvailableLanguages")
      .pipe(catchError((_err) => {
        this.spinnerLoader(true, "lang");
        return EMPTY;
      }))
      .subscribe((list: any[]) => {
        this.langsAvailable = [];
        let chosen = "";
        list.forEach((l) => {
          this.langsAvailable.push({ code: l.code, lang_lang: l.lang_lang });
          if (this.browserLang && l.code === this.browserLang) chosen = l.code;
        });
        if (!chosen)
          chosen =
            this.langsAvailable.find((l) => l.code === this.selLang)?.code ??
            this.defLang;
        this.selLang = chosen;
        this.setCookie("selLang", this.selLang);

        this.mainService
          .getJSON_GET(url + "?act=getLanguageData&lang=" + this.selLang)
          .pipe(catchError((_err) => {
            this.spinnerLoader(true, "lang");
            return EMPTY;
          }))
          .subscribe((data: any[]) => {
            this.langData = data.find((d) => d.code === this.selLang)?.data;
            if (this.langData) {
              if (this.curPage) { this.setTitle(); }

              /* initialize GA only on production (sApiHost starts with "api.") */
              const measurementId = this.langData?.defdata?.ga_measurement_id ?? "";
              if ("" !== measurementId && 0 === this.sApiHost.indexOf("api.")) {
                this.googleAnalyticsService.initialize(measurementId);
              }

              /* wait only for initial images then release spinner */
              if (isPlatformBrowser(this.platformId)) {
                this.zone.runOutsideAngular(() => {
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      this.waitForStartupImages(() =>
                        this.zone.run(() => this.spinnerLoader(true, "lang"))
                      );
                    });
                  });
                });
              } else {
                this.spinnerLoader(true, "lang");
              }
            } else {
              console.error("Missing lang data for", this.selLang);
              this.spinnerLoader(true, "lang");
            }
          });
      });
  }

  /* Wait until every <img> and background in first view loads */
  private waitForStartupImages(done: () => void) {
    const selector =
      ".content img, .content [style*='background'], .footer img, .footer [style*='background']";
    const imgs = Array.from(
      this.document.querySelectorAll(selector)
    ) as HTMLImageElement[];
    if (imgs.length === 0) {
      done();
      return;
    }
    let count = imgs.length;
    const finish = () => {
      if (--count === 0) done();
    };
    imgs.forEach((im) => {
      if (im.complete) {
        ("decode" in im ? im.decode() : Promise.resolve()).finally(finish);
      } else {
        im.onload = im.onerror = finish;
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  dlgAlert, setCookie, setTitle                                     */
  /* ------------------------------------------------------------------ */
  dlgAlert(title: string, msg: string, type: number) {
    const cfg = new MatDialogConfig();
    cfg.disableClose = true;
    cfg.id = "dlgAlertBox";
    cfg.data = {
      title,
      msg,
      closeBtn: this.langData?.defdata?.close ?? "Close",
      type,
    };
    this.matDialog.open(dlgAlert, cfg);
  }

  setCookie(cname: string, cvalue: string, exmin = 0) {
    if (!isPlatformBrowser(this.platformId)) { return; }
    const d = new Date();
    if (exmin === 0) exmin = 365 * 24 * 60;
    d.setTime(d.getTime() + exmin * 60 * 1000);
    this.document.cookie =
      cname + "=" + cvalue + ";expires=" + d.toUTCString() + ";path=/";
  }
  setTitle() {
    this.seoService.setPageSeo(
      this.curPage,
      this.langData,
      this.selLang,
      this.langsAvailable.map((lang) => lang.code)
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Environment params & spinner helpers (unchanged)                  */
  /* ------------------------------------------------------------------ */
  setEnvParams() {
    this.sApiHost = getApiHostByHostName(this.sHostName);
    this.sApiServer = "https://" + this.sApiHost + "/";
  }

  spinnerLoader(act: boolean, code: string) {
    if (act)
      this.allDataLoadedVars = this.allDataLoadedVars.filter((c) => c !== code);
    else this.allDataLoadedVars.push(code);
    this.allDataLoaded = this.allDataLoadedVars.length === 0;
    console.log('[spinner]', act ? 'CLEAR' : 'SET', code, '| pending:', [...this.allDataLoadedVars]);
    if (this.allDataLoaded && isPlatformBrowser(this.platformId)) this.cdr.detectChanges();
  }
  spinnerWaiting(act: boolean, code: string) {
    if (act)
      this.allProcessesDoneVars = this.allProcessesDoneVars.filter(
        (c) => c !== code
      );
    else this.allProcessesDoneVars.push(code);
    this.allProcessesDone = this.allProcessesDoneVars.length === 0;
  }

  /* ------------------------------------------------------------------ */
  /*  Init & simple route handling                                      */
  /* ------------------------------------------------------------------ */
  ngOnInit() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        const routeState = parseRouteState(this.router.url, this.selLang || this.defLang);
        const languageCode = normalizeLanguageCode(this.selLang, routeState.languageCode);

        if (!routeState.hasLanguagePrefix) {
          void this.router.navigateByUrl(buildLocalizedPath(this.router.url, languageCode));

          return;
        }

        this.browserLang = routeState.languageCode;
        this.curPage = routeState.pageKey;

        if (routeState.languageCode !== this.selLang) {
          this.selLang = routeState.languageCode;
          this.setCookie("selLang", this.selLang);
          this.langRequest();

          return;
        }

        this.setTitle();
      });
  }
}
